/*
 * Copyright 2026 Sony Corporation
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface AsyncState<T> {
  data: T | undefined;
  error: Error | undefined;
  loading: boolean;
  reload: () => void;
}

interface State<T> {
  data: T | undefined;
  error: Error | undefined;
  loading: boolean;
}

/**
 * Runs an async read and keeps the last result.
 *
 * The generation guard is the point: a slow request that resolves after the
 * component moved on (a different world, an unmount) must not write its result.
 * Nothing is set synchronously inside the effect, so starting a load cannot
 * cascade into a second render pass before the request is even in flight.
 */
export const useAsync = <T>(load: () => Promise<T>, deps: unknown[]): AsyncState<T> => {
  const [state, setState] = useState<State<T>>({ data: undefined, error: undefined, loading: true });
  const [nonce, setNonce] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    const current = ++generation.current;

    load()
      .then((data) => {
        if (current === generation.current) {
          setState({ data, error: undefined, loading: false });
        }
      })
      .catch((error: Error) => {
        if (current === generation.current) {
          setState((previous) => ({ ...previous, error, loading: false }));
        }
      });

    return () => {
      generation.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => {
    setState((previous) => ({ ...previous, loading: true }));
    setNonce((value) => value + 1);
  }, []);

  return { ...state, reload };
};
